import type { MerchantApiClient } from "../../types.js";
import { dispatchMerchantTelemetry } from "../../shared/telemetry.js";
import { createPoliciesApi, type PoliciesApi } from "./api.js";
import type {
  MerchantPolicyActivationInput,
  MerchantPolicyHistoryFilters,
  MerchantPolicyHistoryList,
  MerchantPolicyPage,
  MerchantPolicySettings,
  MerchantPolicyValidation,
  PolicyOverrideDraft,
  PolicyPreviewResult,
  PolicyPreviewSample,
  PriceMatchPolicy,
  PriceMatchPolicyDto,
} from "./models.js";

export interface PoliciesTelemetry {
  track(eventName: string, payload: Record<string, string | number | boolean | undefined>): void;
}

export interface PoliciesService {
  activateSettings(input: MerchantPolicyActivationInput): Promise<MerchantPolicyPage>;
  loadPolicyHistory(filters: MerchantPolicyHistoryFilters): Promise<MerchantPolicyHistoryList>;
  loadPolicyPage(): Promise<MerchantPolicyPage>;
  loadProductPolicy(productId: string, variantId?: string): Promise<PriceMatchPolicyDto>;
  previewPolicy(
    policy: PriceMatchPolicy,
    sample: PolicyPreviewSample,
  ): Promise<PolicyPreviewResult>;
  deleteProductOverride(productId: string, variantId?: string): Promise<PriceMatchPolicyDto>;
  saveProductOverride(draft: PolicyOverrideDraft): Promise<PriceMatchPolicyDto>;
  validateSettings(settings: MerchantPolicySettings): Promise<MerchantPolicyValidation>;
}

export interface PoliciesServiceOptions {
  api?: PoliciesApi;
  apiClient?: MerchantApiClient;
  idempotencyKeyFactory?: () => string;
  telemetry?: PoliciesTelemetry;
}

export function createPoliciesService(options: PoliciesServiceOptions): PoliciesService {
  const api = options.api ?? createApiFromClient(options.apiClient);
  const telemetry = options.telemetry ?? browserTelemetry();
  const idempotencyKeyFactory = options.idempotencyKeyFactory ?? (() => crypto.randomUUID());

  return {
    activateSettings(input) {
      return track(
        telemetry,
        "merchant_policy_settings_activated",
        () => api.updatePolicySettings(input, idempotencyKeyFactory()),
        {},
      );
    },
    loadPolicyPage() {
      return track(telemetry, "merchant_policy_page_loaded", () => api.getPolicyPage(), {});
    },
    loadPolicyHistory(filters) {
      return track(
        telemetry,
        "merchant_policy_history_loaded",
        () => api.listPolicyHistory(filters),
        { filter: filters.filter, limit: filters.limit },
      );
    },
    loadProductPolicy(productId, variantId) {
      return track(
        telemetry,
        "merchant_product_policy_loaded",
        () => api.getProductPolicy(productId, variantId),
        { productId, variantId },
      );
    },
    previewPolicy(policy, sample) {
      return track(
        telemetry,
        "merchant_policy_previewed",
        () => api.previewPolicy(policy, sample),
        { currency: sample.currency },
      );
    },
    deleteProductOverride(productId, variantId) {
      return track(
        telemetry,
        "merchant_product_policy_override_deleted",
        () => api.deleteProductPolicy(productId, idempotencyKeyFactory(), variantId),
        { productId, variantId },
      );
    },
    saveProductOverride(draft) {
      return track(
        telemetry,
        "merchant_product_policy_override_saved",
        () => api.updateProductPolicy(draft, idempotencyKeyFactory()),
        { scope: draft.scope, scopeId: draft.scopeId, variantId: draft.variantId },
      );
    },
    validateSettings(settings) {
      return track(
        telemetry,
        "merchant_policy_settings_validated",
        () => api.validatePolicySettings(settings),
        { enabled: settings.policyEnabled },
      );
    },
  };
}

async function track<T>(
  telemetry: PoliciesTelemetry,
  eventName: string,
  operation: () => Promise<T>,
  payload: Record<string, string | number | boolean | undefined>,
): Promise<T> {
  const startedAt = performance.now();
  try {
    const result = await operation();
    telemetry.track(eventName, {
      ...payload,
      result: "success",
      durationMs: Math.round(performance.now() - startedAt),
    });
    return result;
  } catch (error: unknown) {
    telemetry.track(eventName, {
      ...payload,
      result: "failure",
      durationMs: Math.round(performance.now() - startedAt),
      errorName: error instanceof Error ? error.name : "unknown",
    });
    throw error;
  }
}

function createApiFromClient(client: MerchantApiClient | undefined): PoliciesApi {
  if (client === undefined) {
    throw new Error("Policies service requires an API client.");
  }

  return createPoliciesApi(client);
}

function browserTelemetry(): PoliciesTelemetry {
  return {
    track(eventName, payload) {
      dispatchMerchantTelemetry(eventName, payload);
    },
  };
}
